import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { AppLayout } from '@/components/layout/AppLayout'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { WorkOrdersPage } from '@/pages/WorkOrdersPage'
import { WorkOrderDetailPage } from '@/pages/WorkOrderDetailPage'
import { AssetsPage } from '@/pages/AssetsPage'
import { AssetDetailPage } from '@/pages/AssetDetailPage'
import { AssetCreatePage } from '@/pages/AssetCreatePage'
import { PPMPage } from '@/pages/PPMPage'
import { CompliancePage } from '@/pages/CompliancePage'
import { VendorsPage } from '@/pages/VendorsPage'
import { AnalyticsPage } from '@/pages/AnalyticsPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { ScanPage } from '@/pages/ScanPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { CreateWorkOrderPage } from '@/pages/CreateWorkOrderPage'
import { SafetyLogPage } from '@/pages/SafetyLogPage'
import { HandoverPage } from '@/pages/HandoverPage'
import { UsersPage } from '@/pages/UsersPage'
import { InventoryPage } from '@/pages/InventoryPage'
import { CasesPage } from '@/pages/CasesPage'
import { RetentionPage } from '@/pages/RetentionPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { PortalPage } from '@/pages/PortalPage'
import { QRScannerPage } from '@/pages/QRScannerPage'
import { OfflinePage } from '@/pages/OfflinePage'
import { AidaChatPage } from '@/pages/AidaChatPage'
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt'
import { PWAUpdatePrompt } from '@/components/PWAUpdatePrompt'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/scan/:tagCode" element={<ScanPage />} />
        <Route path="/offline" element={<OfflinePage />} />

        {/* Protected */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="aida" element={<AidaChatPage />} />
          <Route path="work-orders" element={<WorkOrdersPage />} />
          <Route path="work-orders/new" element={<CreateWorkOrderPage />} />
          <Route path="work-orders/:woId" element={<WorkOrderDetailPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="assets/new" element={<AssetCreatePage />} />
          <Route path="assets/:assetId" element={<AssetDetailPage />} />
          <Route path="ppm" element={<PPMPage />} />
          <Route path="compliance" element={<CompliancePage />} />
          <Route path="safety" element={<SafetyLogPage />} />
          <Route path="handover" element={<HandoverPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="vendors" element={<VendorsPage />} />
          <Route path="cases" element={<CasesPage />} />
          <Route path="retention" element={<RetentionPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="portal" element={<PortalPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* QR Scanner — full screen, outside layout */}
        <Route
          path="/qr-scanner"
          element={
            <RequireAuth>
              <QRScannerPage />
            </RequireAuth>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* PWA prompts */}
      <PWAInstallPrompt />
      <PWAUpdatePrompt />
    </>
  )
}
